import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router';
import { useToggle } from 'react-use';
import axios from 'axios';
import { FaEdit, FaRegTrashAlt } from 'react-icons/fa';
import { isEmpty, orderBy } from 'lodash';
import {
  Modal,
  ModalHeader,
  Flex,
  ModalContent,
  Text,
  ModalFooter,
} from '../design-system';
import { Field, Form } from 'react-final-form';
import { Box, Card, Button } from '../design-system';
import { Table, TableBody, TableCardHeader } from './Table';
import { ERR_MSG } from '../globalConstants';
import Select from './Select';
import Alert from '../helpers/alert';
import { successRefetch } from '../helpers/data-loader';
import ModalForm from './ModalForm';
import {
  getSelectOptions,
  prettyPhone,
  phoneNumberVal,
  emailVal,
} from '../helpers/utils';
import ButtonCellColumn from './ButtonCellColumn';
import TableLink from './TableLink';

const CONTACT_FIELDS = [
  {
    name: 'first_name',
    label: 'First Name',
  },
  {
    name: 'last_name',
    label: 'Last Name',
  },
  {
    name: 'title',
    label: 'Title',
  },
  {
    name: 'company',
    label: 'Company',
  },
  {
    name: 'phone',
    label: 'Phone',
    transformOnBlur: prettyPhone,
    validate: phoneNumberVal,
  },
  {
    name: 'email',
    label: 'Email',
    validate: emailVal,
  },
  {
    name: 'notes',
    label: 'Notes',
    multiline: true,
  },
];

interface ContactListProps {
  modelID: any;
  modelType: string;
  assignedContacts: any[];
  allContacts: any[];
  refetch: () => void;
  canEdit?: boolean;
}

const ContactList = ({
  modelID,
  modelType,
  assignedContacts,
  allContacts,
  refetch,
  canEdit,
}: ContactListProps) => {
  const navigate = useNavigate();
  const [isModalOpen, toggleModal] = useToggle(false);
  const [isContactModalOpen, toggleContactModal] = useToggle(false);
  const [isEditOpen, toggleEditModal] = useToggle(false);
  const [selectedContact, setSelectedContact] = useState<any>();
  const [selectOptions, setOptions] = useState([]);
  const [initialValues, setInitialValues] = useState<any>({
    full_name: '',
    emails: [],
    phones: [],
  });

  useEffect(() => {
    let options = allContacts.map((c) => {
      return {
        label: c.full_name,
        value: c.id,
      };
    });
    options = orderBy(options, ['label'], ['asc']);
    setOptions(options);
  }, [allContacts]);

  const findSelectedContact = (id) => {
    const selected = allContacts.find((c) => c.id === id);

    setSelectedContact(selected);
  };

  const toggleEditContactModal = (id) => {
    const recordInfo = assignedContacts.find((c) => c.record_id === id);
    setInitialValues(recordInfo);

    // have to find all the info for the contact
    const contactInfo = allContacts.find((c) => c.id === recordInfo.id);

    setInitialValues((prevState) => ({
      ...prevState,
      emails: contactInfo.emails,
      phones: contactInfo.phones,
    }));
    toggleEditModal(true);
  };

  const handleSaveContact = async (values) => {
    const payload = { ...values, modelType, modelID };

    try {
      await axios.post(`/contact/${modelID}/storeModelRel`, payload);
      setSelectedContact('');
      toggleModal();
      successRefetch(refetch);
    } catch (error) {
      // console.log(error);
      Alert.error('Assigning contacts require a phone and email');
    }
  };

  const handleUpdateContact = async (values) => {
    const payload = { ...values, modelType };

    try {
      await axios.post(`/contact/${modelID}/updateModelRelation`, payload);
      setInitialValues({ full_name: '', emails: [], phones: [] });
      toggleEditModal();
      successRefetch(refetch);
    } catch (error) {
      Alert.error(ERR_MSG);
    }
  };

  const handleDeleteContact = async (values: any) => {
    const payload: Record<string, any> = {};
    payload.record_id = values;
    payload.modelType = modelType;

    try {
      await axios.post(`/contact/destroyRelation`, payload);
      successRefetch(refetch);
    } catch (error) {
      Alert.error(ERR_MSG);
    }
  };

  const EDIT_MODAL_FIELDS = [
    {
      name: 'selectContact',
      label: 'Contact',
      value: initialValues.full_name,
    },
    {
      name: 'email_id',
      label: 'Email',
      component: 'select',
      value: initialValues.email_id,
      options: getSelectOptions(initialValues.emails),
    },
    {
      name: 'phone_id',
      label: 'Phone',
      component: 'select',
      value: initialValues.phone_id,
      options: getSelectOptions(initialValues.phones),
    },
  ];

  const CONTACT_LIST_COLUMNS = [
    {
      Header: 'Name',
      accessor: 'full_name',
      Cell: ({ cell: { value: contact }, row: { original } }) => {
        return (
          <TableLink to={`/contacts/edit/${original.id}`}>{contact}</TableLink>
        );
      },
    },
    {
      Header: 'Company',
      accessor: 'company',
    },
    {
      Header: 'Email',
      accessor: 'email',
    },
    {
      Header: 'Email Type',
      accessor: 'email_type',
    },
    {
      Header: 'Phone',
      accessor: 'phone',
      Cell: ({
        row: {
          original: { phone },
        },
      }) => prettyPhone(phone),
    },
    {
      Header: 'Phone Type',
      accessor: 'phone_type',
    },
    ...(canEdit
      ? [
          {
            Header: 'Edit',
            disableSortBy: true,
            Cell: ({
              row: {
                original: { record_id },
              },
            }) => (
              <ButtonCellColumn
                onClick={() => toggleEditContactModal(record_id)}
                leftIcon={FaEdit}
              />
            ),
          },
          {
            Header: 'Delete',
            accessor: 'handleDeleteContact',
            disableSortBy: true,
            Cell: ({
              row: {
                original: { record_id },
              },
            }) => (
              <ButtonCellColumn
                onClick={() => handleDeleteContact(record_id)}
                leftIcon={FaRegTrashAlt}
              />
            ),
          },
        ]
      : []),
  ];

  const submitNewContact = async (values) => {
    const {
      data: { id },
    } = await axios.post(`/contact`, values);

    let options = [
      { label: `${values.first_name} ${values.last_name}`, value: id },
      ...selectOptions,
    ];

    // Added the newly added contact to the list the user sees.
    options = orderBy(options, ['label'], ['asc']);
    setOptions(options);

    toggleContactModal();
    // Re-fetch through normal data flow so the parent supplies the updated
    // allContacts (no direct prop mutation).
    refetch();
  };

  return (
    <Card mt={20}>
      {canEdit ? (
        <TableCardHeader
          name="Contacts"
          action="Assign Contact"
          onClick={toggleModal}
        />
      ) : (
        <TableCardHeader name="Contacts" disabled />
      )}
      <TableBody>
        <Table
          name="contacts"
          columns={CONTACT_LIST_COLUMNS}
          data={assignedContacts.map((item) => ({
            ...item,
            toggleEditContactModal,
            handleDeleteContact,
          }))}
        />
      </TableBody>
      <ModalForm
        title="Add New Contact"
        isOpen={isContactModalOpen}
        onSubmit={submitNewContact}
        onClose={toggleContactModal}
        fields={CONTACT_FIELDS}
      />

      <Modal show={isModalOpen} onClose={toggleModal}>
        <ModalHeader>
          <Box mt={2}>
            <Text fontSize={22} fontWeight={600}>
              Assign Contact
            </Text>
            <Button
              style={{ marginTop: 10 }}
              size="sm"
              type="button"
              onClick={toggleContactModal}
            >
              Add New Contact
            </Button>
          </Box>
        </ModalHeader>
        <ModalContent>
          <Form onSubmit={handleSaveContact}>
            {({ handleSubmit, submitting, form }) => {
              return (
                <form onSubmit={handleSubmit}>
                  <Card>
                    <Card.Body>
                      <Flex mt={3}>
                        <Box width="100%">
                          <Field name="selectContact">
                            {({ input }) => (
                              <Select
                                name="contactName"
                                label="Contact Name"
                                {...input}
                                options={selectOptions}
                                onChange={(value) => {
                                  if (value === 'addnew') {
                                    navigate(`/contacts`);
                                  }
                                  input.onChange(value);
                                  findSelectedContact(value);

                                  return form.batch(() => {
                                    const {
                                      values: { selectContact },
                                    } = form.getState();
                                    form.reset({ selectContact });
                                  });
                                }}
                              />
                            )}
                          </Field>
                        </Box>
                      </Flex>
                      {selectedContact && (
                        <div style={{ marginTop: 12 }}>
                          <Flex>
                            <Box width="100%" mt={3} mr={3}>
                              <Field name="email">
                                {({ input }) => (
                                  <Select
                                    name="email"
                                    label="Contact Email"
                                    {...input}
                                    options={getSelectOptions(
                                      selectedContact.emails,
                                    )}
                                  />
                                )}
                              </Field>
                            </Box>

                            <Box width="100%" mt={3} ml={3}>
                              <Field name="phone">
                                {({ input }) => (
                                  <Select
                                    name="phone"
                                    label="Contact Phone"
                                    {...input}
                                    options={getSelectOptions(
                                      selectedContact.phones,
                                    )}
                                  />
                                )}
                              </Field>
                            </Box>
                          </Flex>
                          <ModalFooter>
                            <Button.Group justifyContent="space-between">
                              <Button
                                variant="primaryText"
                                size="xs"
                                type="button"
                                onClick={toggleModal}
                              >
                                Close Without Saving
                              </Button>
                              <Button
                                aria-label="Add Contact"
                                type="submit"
                                disabled={submitting}
                              >
                                Add Contact
                              </Button>
                            </Button.Group>
                          </ModalFooter>
                        </div>
                      )}
                    </Card.Body>
                  </Card>
                </form>
              );
            }}
          </Form>
        </ModalContent>
      </Modal>
      {!isEmpty(initialValues) && (
        <ModalForm
          title="Edit Contact"
          isOpen={isEditOpen}
          onSubmit={handleUpdateContact}
          onClose={toggleEditModal}
          fields={EDIT_MODAL_FIELDS}
          oneColumn
          initialValues={initialValues || {}}
        />
      )}
    </Card>
  );
};

export default ContactList;
